import { Injectable } from '@angular/core';
import { TMachineMaintenanceState } from '../reducers';
import { Store, select } from '@ngrx/store';
import {
  TFilterSelected,
  TREQUEST_DELETE_SPAREPART,
  TREQUEST_BULK_UPDATE_SPAREPART_REQUEST_STATUS,
  TListingFetchReason,
  TREQUEST_SPAREPART_REQUEST_LIST,
  TREQUEST_UPDATE_SPAREPART_THRESHOLD,
  TREQUEST_UPDATE_SPAREPART_CRITICAL,
  TREQUEST_UPDATE_SPAREPART,
  TREQUEST_PARTIAL_APPROVE_SPAREPART_REQUEST,
} from '@monorepo/types';
import * as SparePartListActions from './sparepart-list.actions';
import * as SparePartListSelector from './sparepart-list.selectors';
import { TUserPaginationOption } from '@monorepo/ui';
import { LIST_ITEMS_PER_PAGE_LIST } from '../../constants/constants';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { catchError, finalize, lastValueFrom, map, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SparePartListFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private machineMaintenanceDataService: MachineMaintenanceDataService,
  ) {
    this.initializeSparePartListPagination();
    this.initializeSparePartRequestPagination();
  }

  initializeSparePartListPagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('sparePartListPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setSparePartListPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('sparePartListPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setSparePartListPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  setSparePartListPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('sparePartListPageSize', pagination.pageSize.toString());
    this.store.dispatch(new SparePartListActions.SetSparePartListPagination(pagination));
  }

  resetSparePartListInfo(fetchReason: TListingFetchReason) {
    const pageSize = Number(LOCAL_STORAGE_UTILS.retrieveData('sparePartListPageSize') || LIST_ITEMS_PER_PAGE_LIST[0]);
    this.setSparePartListPagination({ pageNo: 1, pageSize });
    this.fetchSparePartListInfo(fetchReason, []);
  }

  fetchSparePartListInfo(fetchReason: TListingFetchReason, selectedFilters: TFilterSelected[], searchStrOpt?: string) {
    this.store.dispatch(new SparePartListActions.GetSparePartListInfo(fetchReason, selectedFilters, searchStrOpt));
  }

  deleteSparePartListRow(sparePartIds: TREQUEST_DELETE_SPAREPART['sparePartIds']) {
    this.store.dispatch(new SparePartListActions.DeleteSpareParts(sparePartIds));
  }

  updateSparePartListRow(updatedSparePart: TREQUEST_UPDATE_SPAREPART) {
    return this.store.dispatch(new SparePartListActions.UpdateSparePart(updatedSparePart));
  }

  getSparePartListInfo() {
    return this.store.pipe(select(SparePartListSelector.getSparePartListInfo));
  }

  bulkUpdateThreshold(updatedThreshold: TREQUEST_UPDATE_SPAREPART_THRESHOLD) {
    return this.store.dispatch(new SparePartListActions.BulkUpdateSparePartThreshold(updatedThreshold));
  }

  initializeSparePartRequestPagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('sparePartRequestPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setSparePartRequestPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('sparePartRequestPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setSparePartRequestPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  setSparePartRequestPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('sparePartRequestPageSize', pagination.pageSize.toString());
    this.store.dispatch(new SparePartListActions.SetSparePartRequestPagination(pagination));
  }

  fetchSparePartRequestListInfo(
    fetchReason: TListingFetchReason,
    payload: Omit<TREQUEST_SPAREPART_REQUEST_LIST, 'page' | 'itemsPerPage'>,
  ) {
    this.store.dispatch(new SparePartListActions.GetSparePartRequestListInfo(payload, fetchReason));
  }

  updateSparePartRequestStatus(updatedSparePartRequest: TREQUEST_BULK_UPDATE_SPAREPART_REQUEST_STATUS) {
    return this.store.dispatch(new SparePartListActions.BulkUpdateSparePartRequestStatus(updatedSparePartRequest));
  }

  async sparePartRequestPartialApprove(payload: TREQUEST_PARTIAL_APPROVE_SPAREPART_REQUEST) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.sparePartRequestPartialApprove(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  getSparePartRequestListInfo() {
    return this.store.pipe(select(SparePartListSelector.getSparePartRequestListInfo));
  }

  getSparepartListPagination() {
    return this.store.pipe(select(SparePartListSelector.getSparepartListPagination));
  }

  getSparepartRequestPagination() {
    return this.store.pipe(select(SparePartListSelector.getSparepartRequestPagination));
  }

  bulkUpdateCritical(updatedCritical: TREQUEST_UPDATE_SPAREPART_CRITICAL) {
    return this.store.dispatch(new SparePartListActions.BulkUpdateSparePartCritical(updatedCritical));
  }
}

export type TGetSparePartListInfo = ReturnType<SparePartListFacade['getSparePartListInfo']>;
export type TGetSparePartRequestListInfo = ReturnType<SparePartListFacade['getSparePartRequestListInfo']>;
export type TGetSparePartListPagination = ReturnType<SparePartListFacade['getSparepartListPagination']>;
export type TGetSparePartRequestPagination = ReturnType<SparePartListFacade['getSparepartRequestPagination']>;
